<template>
  <div class="subscribe-page">
    <div class="subscribe-pay">
      <main class="subscribe-pay-form">
        <div class="subscribe-pay-inner">
          <header class="subscribe-top">
            <router-link
              :to="{ name: 'home' }"
              class="subscribe-logo"
              aria-label="Sonata home"
            >
              <img
                src="@/assets/sonata-logo.svg"
                width="112"
                height="30"
                alt="Sonata"
              >
            </router-link>
          </header>

          <div
            v-if="complete"
            class="subscribe-done"
          >
            <div class="subscribe-tick">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </div>
            <span class="subscribe-eyebrow">{{ trialMode ? 'Free trial started' : 'Payment received' }}</span>
            <h1>Welcome to {{ group.name }}</h1>
            <p v-if="trialMode">
              Your free trial is active, and you can download and license any track in the catalogue. It runs until <strong>{{ renewalDateText }}</strong>, then you'll be charged {{ chargesText }} until you cancel.
            </p>
            <p v-else>
              Your {{ group.name }} subscription is active, and you can download and license any track in the catalogue. Your next payment of {{ nextChargeText }} is on <strong>{{ renewalDateText }}</strong>.
            </p>
            <router-link
              :to="continueRoute"
              class="subscribe-continue"
            >
              {{ next ? 'Continue where you left off' : 'Start browsing music' }}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </router-link>
          </div>

          <template v-else-if="plan">
            <router-link
              :to="{ name: 'subscribe-plan', query: nextQuery }"
              class="subscribe-back"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              ><path d="M10 3L5 8l5 5" /></svg>
              All plans
            </router-link>
            <div class="subscribe-heading">
              <h1>{{ trialMode ? `Start your ${trialDays}-day free trial` : `Subscribe to ${group.name}` }}</h1>
              <p
                v-if="trialMode"
                class="subscribe-lede"
              >
                {{ afterTrialText }} Cancel anytime.
              </p>
            </div>

            <section class="subscribe-section">
              <h2>Plan</h2>
              <div class="subscribe-select">
                <select
                  :value="group.slug"
                  aria-label="Plan"
                  :disabled="planLocked"
                  @change="changePlan($event.target.value, interval)"
                >
                  <option
                    v-for="option in planOptions"
                    :key="option.slug"
                    :value="option.slug"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div
                class="subscribe-radios"
                role="radiogroup"
                aria-label="Billing period"
              >
                <label
                  v-for="option in intervalOptions"
                  :key="option.interval"
                  class="subscribe-radio"
                  :class="{ 'is-checked': option.interval === interval }"
                >
                  <input
                    type="radio"
                    name="interval"
                    :value="option.interval"
                    :checked="option.interval === interval"
                    :disabled="planLocked"
                    @change="changePlan(group.slug, option.interval)"
                  >
                  <span class="subscribe-radio-what">
                    <b>
                      {{ option.label }}
                      <span
                        v-if="option.badge"
                        class="subscribe-save"
                      >{{ option.badge }}</span>
                    </b>
                    <span>{{ option.note }}</span>
                  </span>
                  <span class="subscribe-radio-amount">
                    <b>{{ option.perMonth }} / month</b>
                    <span v-if="option.billed">{{ option.billed }}</span>
                  </span>
                </label>
              </div>
            </section>

            <section class="subscribe-section">
              <div class="subscribe-section-head">
                <h2>Payment</h2>
                <img
                  src="@/assets/card-brands.png"
                  alt="We accept Visa, Mastercard, American Express and Maestro"
                >
              </div>
              <form
                class="subscribe-section"
                novalidate
                @submit.prevent="submit"
              >
                <div class="subscribe-field">
                  <label
                    class="subscribe-label"
                    for="card-element"
                  >Card details</label>
                  <div
                    id="card-element"
                    ref="card"
                    class="subscribe-card-input"
                    :class="{ 'has-error': errors.card }"
                  />
                  <p
                    v-if="errors.card"
                    class="subscribe-error"
                  >
                    {{ errors.card }}
                  </p>
                </div>
                <div class="subscribe-field">
                  <label
                    class="subscribe-label"
                    for="cc-name"
                  >Name on card</label>
                  <input
                    id="cc-name"
                    v-model.trim="name"
                    type="text"
                    autocomplete="cc-name"
                    class="subscribe-input"
                    :class="{ 'has-error': errors.name }"
                    @input="clearError('name')"
                  >
                  <p
                    v-if="errors.name"
                    class="subscribe-error"
                  >
                    {{ errors.name }}
                  </p>
                </div>
                <div class="subscribe-row">
                  <div class="subscribe-field">
                    <label
                      class="subscribe-label"
                      for="country"
                    >Country</label>
                    <div class="subscribe-select">
                      <select
                        id="country"
                        v-model="country"
                        autocomplete="country"
                      >
                        <option
                          v-for="option in countryOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.display_name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="subscribe-field">
                    <label
                      class="subscribe-label"
                      for="postcode"
                    >{{ country === 'US' ? 'ZIP code' : 'Postcode' }}</label>
                    <input
                      id="postcode"
                      v-model.trim="postcode"
                      type="text"
                      autocomplete="postal-code"
                      class="subscribe-input"
                      :class="{ 'has-error': errors.postcode }"
                      @input="clearError('postcode')"
                    >
                    <p
                      v-if="errors.postcode"
                      class="subscribe-error"
                    >
                      {{ errors.postcode }}
                    </p>
                  </div>
                </div>
                <subscribe-totals
                  class="subscribe-totals-narrow"
                  v-bind="totalsProps"
                  v-on="totalsListeners"
                />
                <p
                  v-if="paymentError"
                  class="subscribe-message"
                  role="alert"
                >
                  {{ paymentError }}
                </p>
                <button
                  type="submit"
                  class="subscribe-button"
                  :disabled="busy || !invoice"
                >
                  <span
                    v-if="busy"
                    class="subscribe-spin"
                  />
                  <span>{{ submitText }}</span>
                </button>
                <p
                  v-if="invoice"
                  class="subscribe-legal"
                >
                  <template v-if="trialMode">
                    <strong>You won't be charged today.</strong> On {{ renewalDateText }} we'll charge {{ chargesText }} until you cancel.
                  </template>
                  <template v-else>
                    <strong>You'll be charged {{ dueTodayText }} today.</strong> Your subscription renews automatically on {{ renewalDateText }} at {{ chargesText }} until you cancel.
                  </template>
                  You can cancel at any time from your account. By {{ trialMode ? 'starting your trial' : 'subscribing' }} you agree to our
                  <router-link
                    :to="{ name: 'user-terms' }"
                    target="_blank"
                  >Terms of Use</router-link>.
                </p>
              </form>
            </section>
          </template>
        </div>
      </main>

      <aside
        v-if="plan"
        class="subscribe-pay-art"
      >
        <img
          src="@/assets/editor-editing.jpg"
          alt=""
        >
        <div class="subscribe-summary-slot">
          <div
            class="subscribe-summary"
            aria-label="Order summary"
          >
            <div class="subscribe-summary-plan">
              <span class="subscribe-eyebrow">{{ interval === 'year' ? 'Yearly' : 'Monthly' }} billing</span>
              <div class="subscribe-summary-name">
                <img
                  v-if="icon"
                  :src="icon"
                  alt=""
                >
                <span class="title-wide">{{ group.name }}</span>
              </div>
              <ul class="subscribe-features">
                <li
                  v-for="feature in group.features.slice(0, 3)"
                  :key="feature"
                >
                  {{ feature }}
                </li>
              </ul>
            </div>
            <subscribe-totals
              v-bind="totalsProps"
              :plan-label="planLabel"
              v-on="totalsListeners"
            >
              <p
                v-if="invoice"
                class="subscribe-note"
              >
                <template v-if="trialMode">
                  Your {{ trialDays }}-day free trial ends on {{ renewalDateText }}. Cancel before then and you won't be charged anything.
                </template>
                <template v-else>
                  Renews automatically {{ everyInterval }} until you cancel. You can cancel at any time.
                </template>
              </p>
            </subscribe-totals>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SubscribeTotals from '@/components/SubscribeTotals.vue'
import subscriptionClient from '@/api/subscriptions.js'
import teamClient from '@/api/teams.js'
import { browseMusicRoute } from '@/utils/catalogue.js'
import { safeNext } from '@/utils/authPages.js'
import { TRIAL_DAYS, formatPrice, groupPlans, monthlyAmount, monthsFreeYearly, planIcon, upcomingCharges, yearlySaving } from '@/utils/subscribeFlow.js'
import { logSubscriptionPurchase, logSubscriptionTrialPurchase } from '@/utils/segment.js'

// Team fields the team update needs alongside the billing country and postcode.
const TEAM_FIELDS = ['name', 'billing_company_name', 'billing_contact', 'address_1', 'address_2', 'city', 'county', 'postal_code', 'country']

// Stripe.js loads asynchronously from index.html, so wait for it on a direct load.
function whenStripeLoaded (timeout = 15000) {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const check = () => {
      if (window.Stripe) {
        resolve(window.Stripe)
      } else if (Date.now() - started > timeout) {
        reject(new Error('Stripe.js did not load'))
      } else {
        setTimeout(check, 50)
      }
    }
    check()
  })
}

function longDate (date) {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default {
  name: 'SubscribePayment',

  components: {
    SubscribeTotals
  },

  data () {
    return {
      trialDays: TRIAL_DAYS,
      name: '',
      country: 'GB',
      postcode: '',
      countryOptions: [],
      errors: {},
      paymentError: null,
      cardComplete: false,
      invoice: null,
      loadingInvoice: false,
      appliedCode: '',
      codeError: null,
      busy: false,
      complete: false,
      // Trial mode when paid; reloading the account afterwards uses the trial up.
      paidWithTrial: null,
      stripe: null,
      card: null,
      cardAttached: false
    }
  },

  metaInfo () {
    return { title: this.trialMode ? 'Start your free trial' : 'Subscribe' }
  },

  computed: {
    ...mapGetters({
      plans: 'subscriptions/plans',
      trialModeNow: 'subscriptions/createSubscriptionWithTrial',
      currentTeam: 'user/currentTeam',
      inProgressSubscription: 'subscriptions/inProgressSubscription'
    }),

    trialMode: function () {
      return this.paidWithTrial === null ? this.trialModeNow : this.paidWithTrial
    },

    plan: function () {
      const pk = String(this.$route.query.plan || '')
      return this.plans.find((plan) => String(plan.pk) === pk) || null
    },

    interval: function () {
      return this.plan ? this.plan.interval : 'year'
    },

    groups: function () {
      return groupPlans(this.plans)
    },

    group: function () {
      return this.groups.find((group) => this.plan && group.slug === this.plan.slug) || null
    },

    icon: function () {
      return this.group ? planIcon(this.group.slug) : null
    },

    planOptions: function () {
      return this.groups
        .filter((group) => group[this.interval])
        .map((group) => {
          const plan = group[this.interval]
          return { slug: group.slug, label: `${group.name}, ${formatPrice(monthlyAmount(plan), plan.currency)} / month` }
        })
    },

    intervalOptions: function () {
      const options = []
      const { month, year } = this.group
      if (month) {
        options.push({ interval: 'month', label: 'Monthly', note: 'Cancel any month', perMonth: formatPrice(month.amount, month.currency) })
      }
      if (year) {
        const saving = yearlySaving(this.group)
        const monthsFree = monthsFreeYearly(this.group)
        options.push({
          interval: 'year',
          label: 'Yearly',
          badge: monthsFree ? `${monthsFree} ${monthsFree === 1 ? 'month' : 'months'} free` : '',
          note: saving ? `Save ${formatPrice(saving, year.currency)} a year` : 'Billed once a year',
          perMonth: formatPrice(monthlyAmount(year), year.currency),
          billed: `${formatPrice(year.amount, year.currency)} billed yearly`
        })
      }
      return options
    },

    // A failed card confirmation leaves an incomplete subscription that a
    // retry reuses, so its plan can't change any more.
    planLocked: function () {
      return Boolean(this.inProgressSubscription) || this.busy
    },

    currency: function () {
      return (this.invoice && this.invoice.currency) || this.plan.currency
    },

    planAmountText: function () {
      return formatPrice(this.plan.amount, this.plan.currency)
    },

    discount: function () {
      if (!this.invoice) {
        return 0
      }
      return (this.invoice.total_discount_amounts || []).reduce((sum, item) => sum + item.amount, 0)
    },

    discountText: function () {
      return formatPrice(this.discount, this.currency)
    },

    dueTodayText: function () {
      return formatPrice(this.invoice ? this.invoice.amount_due : 0, this.currency)
    },

    // The first invoice line covers the first period (or the trial), so it
    // ends when the next payment is due.
    renewalDate: function () {
      const line = this.invoice && this.invoice.lines && this.invoice.lines.data[0]
      return line ? new Date(line.period.end * 1000) : null
    },

    renewalDateText: function () {
      return this.renewalDate ? longDate(this.renewalDate) : ''
    },

    planLabel: function () {
      return `${this.group.name}, ${this.interval === 'year' ? 'yearly' : 'monthly'}`
    },

    totalsProps: function () {
      return {
        trial: this.trialMode,
        dueTodayText: this.invoice ? this.dueTodayText : '',
        nextDateText: this.renewalDateShort,
        planAmountText: `${this.planAmountText} / ${this.interval}`,
        nextAmountText: this.laterChargeText ? this.nextChargeText : `${this.nextChargeText} / ${this.interval}`,
        laterText: this.laterChargeText ? `then ${this.laterChargeText} / ${this.interval}` : '',
        discountText: this.discount ? this.discountText : this.trialDiscountText,
        discountLabel: this.trialDiscountText ? `Discount (${this.appliedCode}), from ${this.renewalDateShort}` : '',
        appliedCode: this.appliedCode,
        codeError: this.codeError || '',
        loading: this.loadingInvoice,
        editable: !this.complete
      }
    },

    totalsListeners: function () {
      return { apply: this.fetchInvoice, remove: () => this.fetchInvoice('') }
    },

    renewalDateShort: function () {
      return this.renewalDate ? this.renewalDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
    },

    // The discount code the preview applied, if any.
    coupon: function () {
      return (this.invoice && this.invoice.discount && this.invoice.discount.coupon) || null
    },

    charges: function () {
      return upcomingCharges(this.plan, this.coupon, { trial: this.trialMode })
    },

    // Nothing is due today on a trial, so a code shows as what it takes off
    // the charge when the trial ends.
    trialDiscountText: function () {
      if (!this.trialMode || !this.coupon || this.charges.next >= this.plan.amount) {
        return ''
      }
      return formatPrice(this.plan.amount - this.charges.next, this.currency)
    },

    nextChargeText: function () {
      return formatPrice(this.charges.next, this.currency)
    },

    laterChargeText: function () {
      return this.charges.later === null ? '' : formatPrice(this.charges.later, this.currency)
    },

    // "£198, then £220 every year" or "£176 every year".
    chargesText: function () {
      if (this.laterChargeText) {
        return `${this.nextChargeText}, then ${this.laterChargeText} ${this.everyInterval}`
      }
      return `${this.nextChargeText} ${this.everyInterval}`
    },

    afterTrialText: function () {
      if (this.laterChargeText) {
        return `Then ${this.nextChargeText} for the first ${this.interval}, then ${this.laterChargeText} a ${this.interval}.`
      }
      return `Then ${this.nextChargeText} a ${this.interval}.`
    },

    everyInterval: function () {
      return this.interval === 'year' ? 'every year' : 'every month'
    },

    submitText: function () {
      if (this.trialMode) {
        return 'Start free trial'
      }
      return this.invoice ? `Pay ${this.dueTodayText} and subscribe` : 'Pay and subscribe'
    },

    next: function () {
      return safeNext(this.$route.query.next)
    },

    nextQuery: function () {
      return this.next ? { next: this.next } : {}
    },

    continueRoute: function () {
      return this.next || browseMusicRoute()
    }
  },

  watch: {
    // Anyone with a subscription already manages it on their subscription
    // page, apart from the one just bought here.
    currentTeam: {
      immediate: true,
      handler (team) {
        if (team && team.current_subscription && !this.complete && !this.busy) {
          this.$router.replace({ name: 'subscription' })
        }
        if (team && team.country) {
          this.country = team.country
        }
        if (team && team.postal_code && !this.postcode) {
          this.postcode = team.postal_code
        }
      }
    },

    // An unknown or missing plan goes back to choosing one, once plans have loaded.
    plans: {
      immediate: true,
      handler (plans) {
        if (plans.length > 0 && !this.plan) {
          this.$router.replace({ name: 'subscribe-plan', query: this.nextQuery })
        }
      }
    },

    plan: {
      immediate: true,
      handler (plan, previous) {
        if (plan && (!previous || plan.pk !== previous.pk)) {
          this.fetchInvoice()
          this.attachCard()
        }
      }
    }
  },

  mounted () {
    teamClient.options()
      .then((response) => {
        this.countryOptions = response.data.actions.POST.country.choices
      })
    this.mountCard()
  },

  beforeDestroy () {
    if (this.card) {
      this.card.destroy()
    }
  },

  methods: {
    mountCard () {
      return whenStripeLoaded()
        .then((Stripe) => {
          this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
          this.card = this.stripe.elements().create('card', {
            // The postcode has its own field, so the card field doesn't ask again.
            hidePostalCode: true,
            style: {
              base: {
                color: '#142136',
                fontFamily: '"Euclid Circular", Avenir, sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': { color: '#a1a6ae' }
              },
              invalid: { color: '#c43434', iconColor: '#c43434' }
            }
          })
          this.card.on('change', (event) => {
            this.cardComplete = event.complete
            if (event.error || event.complete) {
              this.$set(this.errors, 'card', event.error ? event.error.message : null)
            }
          })
          this.attachCard()
        })
        .catch(() => {
          this.paymentError = 'We couldn\'t load the card form. Please refresh the page and try again.'
        })
    },

    // The card field can only go in once both Stripe.js and the form are
    // there; on a fresh load the plans (and so the form) may arrive later.
    attachCard () {
      this.$nextTick(() => {
        if (this.card && !this.cardAttached && this.$refs.card) {
          this.card.mount(this.$refs.card)
          this.cardAttached = true
        }
      })
    },

    changePlan (slug, interval) {
      const group = this.groups.find((g) => g.slug === slug)
      const plan = group && (group[interval] || group.month || group.year)
      if (plan && plan.pk !== this.plan.pk) {
        this.$router.replace({ query: { ...this.$route.query, plan: String(plan.pk) } })
      }
    },

    // Prices the plan with a discount code, or the one already applied.
    fetchInvoice (code = this.appliedCode) {
      this.loadingInvoice = true
      this.codeError = null
      const params = { plan: this.plan.pk, promo_code: code }
      if (this.trialMode) {
        params.with_trial = true
      }
      return subscriptionClient.upcoming(params)
        .then((response) => {
          this.invoice = response.data
          this.appliedCode = code
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.error && error.response.data.error.message
          if (code) {
            // The code was rejected; show the price without it, then say why.
            this.appliedCode = ''
            return this.fetchInvoice('').then(() => {
              this.codeError = message || 'That code couldn\'t be applied.'
            })
          }
          this.paymentError = message || 'We couldn\'t load the price. Please refresh the page and try again.'
        })
        .finally(() => {
          this.loadingInvoice = false
        })
    },

    clearError (field) {
      this.$delete(this.errors, field)
    },

    validate () {
      const errors = {}
      if (!this.cardComplete) {
        errors.card = this.errors.card || 'Enter your card details.'
      }
      if (!this.name) {
        errors.name = 'Enter the name on your card.'
      }
      if (!this.postcode) {
        errors.postcode = this.country === 'US' ? 'Enter your ZIP code.' : 'Enter your postcode.'
      }
      return errors
    },

    teamUpdate () {
      const team = this.currentTeam || {}
      const data = {}
      TEAM_FIELDS.forEach((key) => {
        data[key] = team[key] || ''
      })
      data.country = this.country
      data.postal_code = this.postcode
      return data
    },

    getOrCreateSubscription (params) {
      const existing = this.inProgressSubscription
      if (existing) {
        return Promise.resolve(existing)
      }
      return subscriptionClient.create(params)
        .then((response) => {
          this.$store.dispatch('subscriptions/setInProgressSubscription', response.data)
          return response.data
        })
    },

    // Card payments may need confirming (3D Secure); trials only set up the card.
    confirmSubscription (subscription, paymentMethodId) {
      if (subscription.status === 'incomplete') {
        return this.stripe.confirmCardPayment(subscription.payment_intent_client_secret, { payment_method: paymentMethodId })
          .then((result) => {
            if (result.error) {
              throw result
            }
            return subscriptionClient.activate(subscription.pk, { payment_intent: result.paymentIntent.id })
          })
      }
      if (subscription.setup_intent_client_secret) {
        return this.stripe.confirmCardSetup(subscription.setup_intent_client_secret, { payment_method: paymentMethodId })
          .then((result) => {
            if (result.error) {
              throw result
            }
          })
      }
      return Promise.resolve()
    },

    submit () {
      this.paymentError = null
      this.errors = this.validate()
      if (Object.keys(this.errors).length) {
        return
      }

      this.busy = true
      const plan = this.plan
      const revenue = this.invoice.amount_due / 100.0
      const currency = this.invoice.currency
      const user = this.$store.getters['user/currentUser']

      return teamClient.partialUpdate({ team: this.teamUpdate(), user })
        .then(() => this.stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: { name: this.name, address: { country: this.country, postal_code: this.postcode } }
        }))
        .then((result) => {
          if (result.error) {
            throw result
          }
          const paymentMethodId = result.paymentMethod.id
          const params = { payment_method_id: paymentMethodId, plan: plan.pk, promo_code: this.appliedCode }
          if (this.trialMode) {
            params.with_trial = true
          }
          return this.getOrCreateSubscription(params)
            .then((subscription) => this.confirmSubscription(subscription, paymentMethodId))
        })
        .then(() => {
          if (this.trialMode) {
            logSubscriptionTrialPurchase(plan.product_name, revenue, currency)
          } else {
            logSubscriptionPurchase(plan.product_name, revenue, currency)
          }
          this.paidWithTrial = this.trialMode
          this.complete = true
          this.$store.dispatch('subscriptions/setInProgressSubscription', null)
          // Reload the account so the new subscription is picked up everywhere.
          return this.$store.dispatch('common/initAuthenticated')
        })
        .catch((error) => {
          if (error && error.error) {
            // Stripe.js: a declined or unconfirmed card.
            this.paymentError = error.error.message
          } else if (error && error.response && error.response.data && error.response.data.error) {
            this.paymentError = error.response.data.error.message
          } else {
            this.paymentError = 'Something went wrong taking your payment. Please try again.'
          }
        })
        .finally(() => {
          this.busy = false
        })
    }
  }
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";

.subscribe-pay {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 100vh;

  .subscribe-pay-form {
    padding: 0 16px 56px;
  }

  .subscribe-pay-inner {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 460px;
    margin: 0 auto;
  }

  .subscribe-top {
    padding: 28px 0 0;
  }

  // Repeats the totals above the pay button where the summary card is hidden.
  .subscribe-lines.subscribe-totals-narrow {
    display: none;
    padding: 16px 20px;
    border-radius: 4px;
    background: $bluegrey7;
  }

  .subscribe-heading {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .subscribe-back {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 6px;
    margin-bottom: -16px;
    color: $bluegrey2;
    font-size: 14px;
    text-decoration: none;
    &:hover {
      color: $bluegrey1;
      text-decoration: underline;
    }
  }

  h2 {
    margin: 0;
    color: $bluegrey1;
    font-size: 18px;
    font-weight: 500;
  }

  .subscribe-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .subscribe-section-head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    img {
      width: auto;
      height: 14px;
      filter: grayscale(1);
    }
  }

  .subscribe-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .subscribe-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
  }

  .subscribe-label {
    color: $bluegrey2;
    font-size: 14px;
  }

  .subscribe-input,
  .subscribe-select select {
    width: 100%;
    height: 50px;
    padding: 0 14px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: $bluegrey7;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    font: 16px $family-sans-serif;
    color: $bluegrey1;
    appearance: none;
    &:focus {
      outline: none;
      border-color: $bluegrey1;
      background: $white;
    }
    &[disabled] {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .subscribe-select {
    position: relative;
    select {
      padding-right: 44px;
      cursor: pointer;
    }
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      right: 18px;
      width: 8px;
      height: 8px;
      margin-top: -6px;
      border-right: 2px solid $bluegrey2;
      border-bottom: 2px solid $bluegrey2;
      transform: rotate(45deg);
      pointer-events: none;
    }
  }

  // Stripe mounts its card field inside this box.
  .subscribe-card-input {
    padding: 16px 14px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: $bluegrey7;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    &.StripeElement--focus {
      border-color: $bluegrey1;
      background: $white;
    }
  }

  .has-error {
    border-color: rgb(214, 64, 64) !important;
    background: #fdf0f0 !important;
  }

  .subscribe-error {
    margin: 0;
    color: rgb(196, 52, 52);
    font-size: 13px;
  }

  .subscribe-message {
    margin: 0;
    padding: 12px 14px;
    border-radius: 4px;
    background: #fdf0f0;
    color: rgb(196, 52, 52);
    font-size: 14px;
  }

  .subscribe-radios {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid $bluegrey6;
    border-radius: 4px;
  }

  .subscribe-radio {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    background: $white;
    cursor: pointer;
    & + .subscribe-radio {
      border-top: 1px solid $bluegrey6;
    }
    &.is-checked {
      background: $bluegrey7;
    }
    input {
      flex: none;
      width: 20px;
      height: 20px;
      margin: 0;
      accent-color: $bluegrey0;
    }
    b {
      color: $bluegrey1;
      font-size: 15px;
      font-weight: 500;
    }
  }

  .subscribe-radio-what {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: 13px;
    b {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
  }

  .subscribe-radio-amount {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-size: 13px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .subscribe-legal {
    margin: 0;
    color: $bluegrey3;
    font-size: 12px;
    line-height: 1.6;
    a {
      color: $bluegrey2;
      text-decoration: underline;
    }
    strong {
      color: $bluegrey1;
      font-weight: 500;
    }
  }

  .subscribe-spin {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: subscribe-spin 0.7s linear infinite;
  }

  .subscribe-done {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 48px;
    p {
      margin: 0;
      color: $bluegrey2;
      font-size: 16px;
      line-height: 1.5;
    }
    a {
      text-decoration: underline;
    }
    a.subscribe-continue {
      display: inline-flex;
      align-self: flex-start;
      align-items: center;
      gap: 10px;
      height: 46px;
      margin-top: 8px;
      padding: 0 24px 0 28px;
      border: 1.5px solid $bluegrey1;
      border-radius: 999px;
      color: $bluegrey1;
      font: 500 15px $family-sans-serif;
      text-decoration: none;
      transition: background 0.12s ease-out;
      svg {
        transition: transform 0.12s ease-out;
      }
      &:hover {
        background: $bluegrey7;
        svg {
          transform: translateX(2px);
        }
      }
    }
  }

  .subscribe-tick {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: $bluegrey7;
    color: rgb(34, 160, 92);
  }

  .subscribe-pay-art {
    position: relative;
    overflow: hidden;
    background: $bluegrey1;
    > img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      max-width: none;
      object-fit: cover;
      object-position: 50% 35%;
    }
  }

  // The summary sits at the top of the image, over the window behind the editor.
  .subscribe-summary-slot {
    position: absolute;
    inset: 0 0 auto 0;
    display: flex;
    justify-content: center;
    padding: 32px;
  }

  .subscribe-summary {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 440px;
    overflow: hidden;
    border-radius: 4px;
    background: $white;
    box-shadow: 0 20px 50px rgba(6, 11, 18, 0.25);
  }

  .subscribe-summary-plan {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 24px;
    background: $bluegrey7;
    .subscribe-features {
      gap: 6px;
      margin: 0;
      padding: 0;
      border: 0;
      li {
        font-size: 13px;
      }
    }
  }

  .subscribe-summary-name {
    display: flex;
    align-items: center;
    gap: 12px;
    color: $bluegrey1;
    font-size: 18px;
    img {
      width: 36px;
      height: 36px;
    }
  }

  .subscribe-lines {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 24px 24px;
    font-variant-numeric: tabular-nums;
  }

  .subscribe-line {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 14px;
    // Amounts stay on one line; long labels wrap instead.
    > span:last-child {
      flex: none;
      white-space: nowrap;
    }
    &.is-discount {
      color: rgb(26, 128, 72);
    }
    // Due today, and what comes next as prominently.
    &.is-total {
      color: $bluegrey1;
      font-size: 17px;
      font-weight: 500;
    }
    &.is-total:not(.is-next) {
      padding-top: 16px;
      border-top: 1px solid $bluegrey6;
    }
  }

  .subscribe-then {
    margin: -6px 0 0;
    color: $bluegrey2;
    font-size: 13px;
    text-align: right;
  }

  .subscribe-link-button {
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: none;
    color: $bluegrey1;
    font: 14px $family-sans-serif;
    text-decoration: underline;
    cursor: pointer;
  }

  .subscribe-code {
    display: flex;
    gap: 8px;
    .subscribe-input {
      height: 40px;
      font-size: 14px;
      text-transform: uppercase;
    }
    button {
      flex: none;
      height: 40px;
      padding: 0 16px;
      border: 1px solid $bluegrey5;
      border-radius: 999px;
      background: $white;
      color: $bluegrey1;
      font: 500 14px $family-sans-serif;
      cursor: pointer;
    }
  }

  .subscribe-note {
    margin: 0;
    color: $bluegrey2;
    font-size: 13px;
    line-height: 1.5;
  }

  // On narrow screens the summary sits above the form, without the image or features.
  // On narrow screens the image and summary card go, and the totals sit
  // above the pay button instead.
  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    .subscribe-pay-art {
      display: none;
    }
    .subscribe-lines.subscribe-totals-narrow {
      display: flex;
    }
    .subscribe-top {
      padding-top: 20px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .subscribe-spin {
      animation-duration: 2s;
    }
  }
}

@keyframes subscribe-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
